import { useMutation, useQueryClient } from "@tanstack/react-query";
import { callApi, callApiWithOptions, HttpMethod } from "src/services/apiService";
import { toast } from "sonner";
import { TC, phoneEndpoint, carrierEndpoint } from "../utils/trustCallApi";

export function usePhoneActions() {
  const queryClient = useQueryClient();

  const invalidatePhones = () => {
    queryClient.refetchQueries({ queryKey: ["trustcall", "phones", "active"] });
  };

  const addPhone = useMutation({
    mutationFn: (data: { phone: string; label?: string; serviceType: number; legalEntityId?: number | null }) =>
      callApi(TC.PHONES, HttpMethod.POST, [
        { Phone: data.phone, Label: data.label ?? "", ServiceType: data.serviceType, TrustCallLegalEntity: data.legalEntityId },
      ]),
    onSuccess: () => {
      invalidatePhones();
      toast.success("Phone number added successfully");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to add phone number"),
  });

  const addPhones = useMutation({
    mutationFn: (phones: { Phone: string; Label: string; ServiceType: number; TrustCallLegalEntity: number | null }[]) =>
      callApi(TC.PHONES, HttpMethod.POST, phones),
    onSuccess: () => {
      invalidatePhones();
    },
    onError: (error: Error) => toast.error(error.message || "Failed to add phone numbers"),
  });

  const uploadPhonesCsv = useMutation({
    mutationFn: (data: { file: File; serviceType: number; legalEntityId?: number; updateExisting: boolean }) =>
      callApiWithOptions({
        endpoint: TC.PHONES_UPLOAD,
        method: HttpMethod.POST,
        file: data.file,
        body: {
          serviceType: data.serviceType,
          ...(data.legalEntityId != null && { legalEntityId: data.legalEntityId }),
          updateExistingNumber: data.updateExisting,
        },
      }),
    onSuccess: (result) => {
      invalidatePhones();
      if (result.backgroundMessage) {
        toast.success(result.backgroundMessage);
      } else {
        toast.success(`${result.added} phone number(s) added`);
      }
    },
    onError: (error: Error) => toast.error(error.message || "Failed to upload CSV"),
  });

  const bulkDeleteUpload = useMutation({
    mutationFn: (file: File) =>
      callApiWithOptions({
        endpoint: TC.PHONES_BULK_DELETE_UPLOAD,
        method: HttpMethod.POST,
        file,
      }),
    onSuccess: () => {
      invalidatePhones();
    },
    onError: (error: Error) => toast.error(error.message || "Failed to upload bulk remove CSV"),
  });

  const deletePhone = useMutation({
    mutationFn: (phoneNumber: string) =>
      callApi(`${TC.PHONES}/${encodeURIComponent(phoneNumber)}`, HttpMethod.DELETE),
    onSuccess: () => {
      invalidatePhones();
      toast.success("Phone number deleted");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to delete phone number"),
  });

  const bulkDeletePhones = useMutation({
    mutationFn: (trustcallIds: string[]) =>
      callApi(TC.PHONES_BULK_DELETE, HttpMethod.POST, trustcallIds),
    onSuccess: (result, trustcallIds) => {
      invalidatePhones();
      // Server returns the actual rows deleted; warn when it falls short of the
      // selection (e.g. rows already removed) instead of claiming full success.
      if (result.deleted < trustcallIds.length) {
        toast.warning(`Only ${result.deleted} of ${trustcallIds.length} phone number(s) deleted`);
      } else {
        toast.success(`${result.deleted} phone number(s) deleted`);
      }
    },
    onError: (error: Error) => toast.error(error.message || "Failed to delete phone numbers"),
  });

  const updateLabel = useMutation({
    mutationFn: (data: { phoneNumber: string; label: string }) =>
      callApi(`${phoneEndpoint(data.phoneNumber, "label")}`, HttpMethod.PATCH, { label: data.label }),
    onSuccess: () => {
      invalidatePhones();
      toast.success("Label updated");
    },
    onError: (error: any) => {
      const detail = error?.data?.message || error?.message || "Failed to update label";
      console.error("UpdateLabel failed:", error);
      toast.error(detail);
    },
  });

  const updateServiceType = useMutation({
    mutationFn: (data: { phoneNumber: string; serviceType: string }) =>
      callApi(`${phoneEndpoint(data.phoneNumber, "service-type")}`, HttpMethod.PATCH, { serviceType: data.serviceType }),
    onSuccess: () => {
      invalidatePhones();
      toast.success("Service type updated");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to update service type"),
  });

  const updateLegalEntity = useMutation({
    mutationFn: (data: { phoneNumber: string; legalEntityId: number }) =>
      callApi(`${phoneEndpoint(data.phoneNumber, "legal-entity")}`, HttpMethod.PATCH, { legalEntityId: data.legalEntityId }),
    onSuccess: () => {
      invalidatePhones();
      toast.success("Legal entity updated");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to update legal entity"),
  });

  const rescanPhones = useMutation({
    mutationFn: (trustcallIds: string[]) =>
      callApi(TC.CARRIERS_RESCAN, HttpMethod.POST, trustcallIds),
    onSuccess: (result) => {
      invalidatePhones();
      toast.success(`${result.queued} phone number(s) queued for rescan`);
    },
    onError: (error: Error) => toast.error(error.message || "Failed to rescan"),
  });

  const rescanAllCarriers = useMutation({
    mutationFn: (data: { scanATT: boolean; scanTMobile: boolean; scanVerizon: boolean }) =>
      callApi(TC.CARRIERS_RESCAN_ALL, HttpMethod.POST, data),
    onSuccess: (result) => {
      invalidatePhones();
      toast.success(`${result.queued} phone number(s) queued for rescan`);
    },
    onError: (error: Error) => toast.error(error.message || "Failed to rescan carriers"),
  });

  const bulkAddTnrm = useMutation({
    mutationFn: (trustcallIds: string[]) =>
      callApi(TC.CARRIERS_BULK_TNRM_ADD, HttpMethod.POST, trustcallIds),
    onSuccess: (result) => {
      invalidatePhones();
      toast.success(`${result.bulkAddCount} phone number(s) added to TNRM`);
    },
    onError: (error: Error) => toast.error(error.message || "Failed to add to TNRM"),
  });

  const bulkRemoveTnrm = useMutation({
    mutationFn: (trustcallIds: string[]) =>
      callApi(TC.CARRIERS_BULK_TNRM_REMOVE, HttpMethod.POST, trustcallIds),
    onSuccess: (result) => {
      invalidatePhones();
      toast.success(`${result.bulkRemoveCount} phone number(s) removed from TNRM`);
    },
    onError: (error: Error) => toast.error(error.message || "Failed to remove from TNRM"),
  });

  const updateTnrm = useMutation({
    mutationFn: (data: { phoneNumber: string; isAdd: boolean }) =>
      callApi(`${carrierEndpoint(data.phoneNumber, "tnrm")}`, HttpMethod.POST, { isAdd: data.isAdd }),
    onSuccess: (_, variables) => {
      invalidatePhones();
      toast.success(`TNRM ${variables.isAdd ? "added" : "removed"}`);
    },
    onError: (error: Error) => toast.error(error.message || "Failed to update TNRM"),
  });

  return {
    addPhone,
    addPhones,
    uploadPhonesCsv,
    bulkDeleteUpload,
    deletePhone,
    bulkDeletePhones,
    updateLabel,
    updateServiceType,
    updateLegalEntity,
    rescanPhones,
    rescanAllCarriers,
    bulkAddTnrm,
    bulkRemoveTnrm,
    updateTnrm,
  };
}
